import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { api, errCode, formatXAF } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";

const Chips = ({ items, value, onChange, testId }) => (
  <div className="flex flex-wrap gap-2">
    {items.map(([v, label]) => (
      <button key={v} type="button" onClick={() => onChange(v)} data-testid={`${testId}-${v}`}
        className={`h-10 rounded-full px-4 text-sm font-semibold transition-colors ${value === v ? "bg-amber-500 text-[#0B0D12]" : "border border-white/10 text-slate-300"}`}>{label}</button>
    ))}
  </div>
);

const useCountdown = (iso) => {
  const [n, setN] = useState(Date.now());
  useEffect(() => { const i = setInterval(() => setN(Date.now()), 1000); return () => clearInterval(i); }, []);
  const s = Math.max(0, Math.floor((new Date(iso) - n) / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};

const cadLabel = (t, k, c) => `${t(`dames.cad_${k}`)} · ${c[0] / 60} min + ${c[1]} s`;

const InviteRow = ({ inv, reload }) => {
  const { t, tErr } = useI18n();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const left = useCountdown(inv.expires_at);
  const link = `${window.location.origin}/lobby?game=dames&code=${inv.code}`;
  const act = async (a) => {
    try {
      const { data } = await api.post(`/dames/invites/${inv.code}/${a}`);
      qc.invalidateQueries({ queryKey: ["wallet"] });
      if (data.game_id) navigate(`/dames/game/${data.game_id}`); else reload();
    } catch (e) { toast.error(tErr(errCode(e))); }
  };
  return (
    <div className="surface space-y-2 p-4 text-sm" data-testid={`dames-invite-${inv.code}`}>
      <div className="flex items-center justify-between gap-2">
        <b>{inv.mine ? (inv.invitee ? t("dames.toPlayer", { p: inv.invitee }) : t("dames.openInvite")) : t("dames.fromPlayer", { p: inv.creator })}</b>
        <span className="font-mono text-xs text-amber-300" data-testid="dames-invite-countdown">{left}</span>
      </div>
      <p className="text-slate-400">{formatXAF(inv.stake)} · {cadLabel(t, inv.cadence, inv.clock)} · {t("dames.gain")} {formatXAF(inv.gain)}</p>
      {inv.mine ? (
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-lg tracking-[0.25em] text-amber-300" data-testid="dames-invite-code">{inv.code}</span>
          <a href={`https://wa.me/?text=${encodeURIComponent(`${t("dames.shareText", { code: inv.code })} ${link}`)}`} target="_blank" rel="noreferrer"
            className="inline-flex h-9 items-center gap-1 rounded-full bg-[#25D366] px-3 font-semibold text-[#0B0D12]" data-testid="dames-invite-whatsapp"><MessageCircle className="h-4 w-4" /> WhatsApp</a>
          <button type="button" onClick={() => act("cancel")} className="h-9 rounded-full border border-white/15 px-3" data-testid="dames-invite-cancel">{t("dames.cancel")}</button>
        </div>
      ) : (
        <div className="flex gap-2">
          <button type="button" onClick={() => act("accept")} className="h-10 flex-1 rounded-full bg-amber-500 font-bold text-[#0B0D12]" data-testid="dames-invite-accept">{t("dames.accept", { s: formatXAF(inv.stake) })}</button>
          {inv.invitee_id && <button type="button" onClick={() => act("decline")} className="h-10 rounded-full border border-white/15 px-4" data-testid="dames-invite-decline">{t("dames.decline")}</button>}
        </div>
      )}
    </div>
  );
};

const SeekRow = ({ inv, reload }) => {
  const { t, tErr } = useI18n();
  const left = useCountdown(inv.expires_at);
  const stop = async () => {
    try { await api.post(`/dames/invites/${inv.code}/cancel`); reload(); } catch (e) { toast.error(tErr(errCode(e))); }
  };
  return (
    <div className="surface space-y-3 p-5" data-testid="dames-seek-active">
      <div className="flex items-center justify-between gap-2">
        <b className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin text-amber-400" />{t("dames.seeking")}</b>
        <span className="font-mono text-xs text-amber-300" data-testid="dames-seek-countdown">{left}</span>
      </div>
      <p className="text-sm text-slate-400">{formatXAF(inv.stake)} · {cadLabel(t, inv.cadence, inv.clock)} · {t("dames.gain")} {formatXAF(inv.gain)}</p>
      <button type="button" onClick={stop} className="h-10 rounded-full border border-white/15 px-4 text-sm" data-testid="dames-seek-cancel">{t("dames.seekCancel")}</button>
    </div>
  );
};

const SeekExpired = ({ s, cfg, onSeek, onQuit }) => {
  const { t } = useI18n();
  return (
    <div className="surface space-y-3 border border-amber-500/30 p-5" data-testid="dames-seek-expired">
      <p className="text-sm">{t("dames.seekNone", { s: formatXAF(s.stake), c: cadLabel(t, s.cadence, cfg.dames_cadences[s.cadence] || [0, 0]) })}</p>
      <div className="flex gap-2">
        <button type="button" onClick={() => onSeek(s.stake, s.cadence)} className="h-10 flex-1 rounded-full bg-amber-500 font-bold text-[#0B0D12]" data-testid="dames-seek-again">{t("dames.seekAgain")}</button>
        <button type="button" onClick={onQuit} className="h-10 rounded-full border border-white/15 px-4 text-sm" data-testid="dames-seek-quit">{t("dames.seekQuit")}</button>
      </div>
    </div>
  );
};

const CodeInvite = ({ code, onClose }) => {
  const { tErr } = useI18n();
  const { data, error, refetch } = useQuery({ queryKey: ["dames-inv", code], queryFn: () => api.get(`/dames/invites/${code}`).then((r) => r.data), retry: false, refetchInterval: 3000 });
  const navigate = useNavigate();
  useEffect(() => { if (data?.game_id) navigate(`/dames/game/${data.game_id}`); }, [data, navigate]);
  if (error) return <div className="surface p-4 text-sm text-red-300" data-testid="dames-code-error">{tErr(errCode(error))} <button className="ml-2 underline" onClick={onClose}>OK</button></div>;
  if (!data || data.status !== "invited") return null;
  return <InviteRow inv={data} reload={refetch} />;
};

const History = () => {
  const { t } = useI18n();
  const { data } = useQuery({ queryKey: ["dames-history"], queryFn: () => api.get("/dames/history").then((r) => r.data) });
  if (!data?.length) return null;
  const label = (g) => {
    if (g.status === "cancelled") return [t("dames.histCancelled"), "text-slate-400"];
    if (g.result === "draw") return [`${t("dames.histDraw")} · ${formatXAF(g.won)}`, "text-slate-300"];
    return g.won > 0 ? [`+${formatXAF(g.won - g.stake)}`, "text-emerald-400"] : [`${t("dames.histLost")} · -${formatXAF(g.stake)}`, "text-red-300"];
  };
  return (
    <div className="surface space-y-2 p-5" data-testid="dames-history">
      <p className="font-display font-bold">{t("dames.history")}</p>
      {data.slice(0, 10).map((g) => {
        const [txt, cls] = label(g);
        return (
          <Link key={g.id} to={`/dames/game/${g.id}`} className="flex items-center justify-between gap-2 rounded-xl bg-white/[0.03] px-3 py-2 text-sm" data-testid={`dames-history-${g.id}`}>
            <span className="truncate">{t("dames.vs", { p: g.opponent })} · {formatXAF(g.stake)}</span>
            <span className={`shrink-0 ${cls}`}>{txt}</span>
          </Link>
        );
      })}
    </div>
  );
};

export const DamesPanel = () => {
  const { t, tErr } = useI18n();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const code = params.get("code");
  const { data: cfg } = useQuery({ queryKey: ["dames-cfg"], queryFn: () => api.get("/dames/config").then((r) => r.data) });
  const { data: inv, refetch } = useQuery({ queryKey: ["dames-invites"], queryFn: () => api.get("/dames/invites").then((r) => r.data), refetchInterval: 3000 });
  const [f, setF] = useState({ stake: 250, cadence: "blitz", color: "random", pseudo: "" });
  const [codeIn, setCodeIn] = useState("");
  const [hidden, setHidden] = useState(null);
  useEffect(() => {
    const check = () => api.get("/dames/me").then(({ data }) => { if (data.game_id) navigate(`/dames/game/${data.game_id}`); }).catch(() => {});
    check();
    const i = setInterval(check, 3000);
    return () => clearInterval(i);
  }, [navigate]);
  const prevSent = useRef(0);
  useEffect(() => {
    const n = (inv?.sent || []).length;
    if (n < prevSent.current) qc.invalidateQueries({ queryKey: ["wallet"] });
    prevSent.current = n;
  }, [inv, qc]);
  if (!cfg) return null;
  const pot = f.stake * 2, com = Math.floor(pot * cfg.dames_commission_percent / 100);
  const create = async () => {
    try {
      await api.post("/dames/invites", { stake: f.stake, cadence: f.cadence, color: f.color, pseudo: f.pseudo.trim() || null });
      qc.invalidateQueries({ queryKey: ["wallet"] });
      refetch();
    } catch (e) { toast.error(tErr(errCode(e))); }
  };
  const busy = (inv?.sent || []).length > 0;
  const mySeek = (inv?.sent || []).find((i) => i.public);
  const expired = !busy && inv?.expired_seek && inv.expired_seek.id !== hidden ? inv.expired_seek : null;
  const seek = async (stake, cadence) => {
    if (inv?.expired_seek) setHidden(inv.expired_seek.id);
    try {
      const { data } = await api.post("/dames/seek", { stake, cadence });
      if (data.game_id) navigate(`/dames/game/${data.game_id}`); else refetch();
    } catch (e) { toast.error(tErr(errCode(e))); }
  };
  return (
    <div className="space-y-5" data-testid="dames-panel">
      {code && <CodeInvite code={code.toUpperCase()} onClose={() => setParams({ game: "dames" })} />}
      {(inv?.received || []).map((i) => <InviteRow key={i.code} inv={i} reload={refetch} />)}
      {mySeek && <SeekRow inv={mySeek} reload={refetch} />}
      {(inv?.sent || []).filter((i) => !i.public).map((i) => <InviteRow key={i.code} inv={i} reload={refetch} />)}
      {expired && <SeekExpired s={expired} cfg={cfg} onSeek={seek} onQuit={() => setHidden(expired.id)} />}
      {!busy && (
        <div className="surface space-y-4 p-5" data-testid="dames-create-form">
          <p className="font-display text-lg font-bold">{t("dames.seekTitle")}</p>
          <p className="text-sm text-slate-400">{t("dames.seekSub")}</p>
          <Chips items={cfg.dames_stakes.map((s) => [s, formatXAF(s)])} value={f.stake} onChange={(stake) => setF({ ...f, stake })} testId="dames-stake" />
          <Chips items={Object.entries(cfg.dames_cadences).map(([k, c]) => [k, cadLabel(t, k, c)])} value={f.cadence} onChange={(cadence) => setF({ ...f, cadence })} testId="dames-cadence" />
          <p className="text-sm text-slate-300" data-testid="dames-preview">{t("dames.preview", { pot: formatXAF(pot), com: formatXAF(com), gain: formatXAF(pot - com) })}</p>
          <button type="button" onClick={() => seek(f.stake, f.cadence)} className="h-12 w-full rounded-full bg-amber-500 font-bold text-[#0B0D12]" data-testid="dames-seek-btn">{t("dames.seekBtn", { s: formatXAF(f.stake) })}</button>
          <div className="space-y-3 border-t border-white/10 pt-4">
            <p className="font-display font-bold">{t("dames.orPrivate")}</p>
            <Chips items={[["white", t("dames.white")], ["black", t("dames.black")], ["random", t("dames.random")]]} value={f.color} onChange={(color) => setF({ ...f, color })} testId="dames-color" />
            <input value={f.pseudo} onChange={(e) => setF({ ...f, pseudo: e.target.value })} placeholder={t("dames.pseudoPh")} maxLength={40}
              className="h-11 w-full rounded-full border border-white/10 bg-white/5 px-4" data-testid="dames-pseudo-input" />
            <button type="button" onClick={create} className="h-12 w-full rounded-full border border-amber-500/60 font-bold text-amber-300" data-testid="dames-create-btn">{t("dames.createBtn", { s: formatXAF(f.stake) })}</button>
          </div>
        </div>
      )}
      <form className="surface flex gap-2 p-4" onSubmit={(e) => { e.preventDefault(); if (codeIn.trim()) setParams({ game: "dames", code: codeIn.trim().toUpperCase() }); }}>
        <input value={codeIn} onChange={(e) => setCodeIn(e.target.value)} placeholder={t("dames.codePh")} maxLength={8}
          className="h-11 min-w-0 flex-1 rounded-full border border-white/10 bg-white/5 px-4 font-mono uppercase" data-testid="dames-code-input" />
        <button type="submit" className="h-11 rounded-full bg-white/10 px-5 text-sm font-semibold" data-testid="dames-code-btn">{t("ludo.go")}</button>
      </form>
      <Link to="/dames/essai" className="block text-center text-sm text-amber-300 underline" data-testid="lobby-dames-practice-btn">{t("dames.practice")}</Link>
      <History />
    </div>
  );
};
